import React from 'react';
import type { AdminUserResponse } from '../services/organisationService';

interface AdminsListProps {
  admins: AdminUserResponse[];
  loading?: boolean;
}

/**
 * AdminsList Component
 * 
 * Displays a list of all administrators in the organization with their details.
 * This component addresses the client's requirement for transparency in admin permissions.
 * 
 * Features:
 * - Shows admin name, email, role, and join date
 * - Responsive table design
 * - Loading state handling
 * - Empty state messaging
 * - Accessible table structure
 */
export const AdminsList: React.FC<AdminsListProps> = ({ admins, loading = false }) => {
  // Format date for display
  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'Not available';
    try {
      return new Date(dateString).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  // Get full name with fallback to email
  const getDisplayName = (admin: AdminUserResponse): string => {
    if (admin.first_name && admin.last_name) {
      return `${admin.first_name} ${admin.last_name}`;
    }
    if (admin.first_name) {
      return admin.first_name;
    }
    // Extract name from email for better display
    const emailParts = admin.email.split('@');
    const emailName = emailParts[0] || admin.email;
    return emailName.replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get role display with fallback
  const getRoleDisplay = (role?: string): string => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'admin':
        return 'Admin';
      default:
        return 'Admin';
    }
  };

  // Get role badge styling
  const getRoleBadgeClass = (role?: string): string => {
    switch (role) {
      case 'super_admin':
        return 'badge badge-warning';
      case 'admin':
        return 'badge badge-primary';
      default:
        return 'badge badge-primary';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <span className="loading loading-spinner loading-lg"></span>
        <span className="ml-2">Loading administrators...</span>
      </div>
    );
  }

  if (!admins || admins.length === 0) {
    return (
      <div className="text-center py-8 text-base-content/70">
        <div className="mb-2">
          <svg className="w-12 h-12 mx-auto opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        </div>
        <p>No administrators found</p>
        <p className="text-sm">This organization doesn't have any admins assigned yet.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="table table-zebra w-full">
        {/* Table header with semantic HTML for accessibility */}
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Added</th>
          </tr>
        </thead>
        <tbody>
          {admins.map((admin) => (
            <tr key={admin.id} className="hover">
              {/* Name column - displays full name or email as fallback */}
              <td>
                <div className="font-medium">
                  {getDisplayName(admin)}
                </div>
                {/* Show email as secondary info if name is available */}
                {admin.first_name && (
                  <div className="text-sm text-base-content/70">
                    {admin.email}
                  </div>
                )}
              </td>
              
              {/* Email column - primary contact info */}
              <td>
                <a 
                  href={`mailto:${admin.email}`}
                  className="link link-primary link-hover"
                  title={`Send email to ${admin.email}`}
                >
                  {admin.email}
                </a>
              </td>
              
              {/* Role column - with color-coded badges */}
              <td>
                <span className={getRoleBadgeClass(admin.role)}>
                  {getRoleDisplay(admin.role)}
                </span>
              </td>
              
              {/* Added date column - formatted for readability */}
              <td>
                <div className="text-sm">
                  {formatDate(admin.created_at)}
                </div>
                {admin.last_login && (
                  <div className="text-xs text-base-content/50">
                    Last login: {formatDate(admin.last_login)}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      {/* Summary footer */}
      <div className="mt-4 text-sm text-base-content/70">
        Showing {admins.length} admin{admins.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};
